import DOMPurify, { type Config } from "dompurify";

/**
 * DOMPurify needs a DOM. During SSR/prerender there is none, so we fall back to
 * a conservative allowlist sanitizer that strips every tag and attribute the
 * browser-side config would strip.
 */
export function sanitizeHtml(html: string, config: Config & { ALLOWED_TAGS?: string[] }): string {
  const canUseDomPurify =
    typeof window !== "undefined" && typeof (DOMPurify as { sanitize?: unknown }).sanitize === "function";

  if (canUseDomPurify) {
    return DOMPurify.sanitize(html, config) as unknown as string;
  }

  const allowed = new Set((config.ALLOWED_TAGS ?? []).map((t) => t.toLowerCase()));
  const allowedAttrs = new Set((config.ALLOWED_ATTR ?? []).map((a) => a.toLowerCase()));

  return html
    .replace(/<\s*(script|style|iframe|object|embed)[\s\S]*?<\s*\/\s*\1\s*>/gi, "")
    .replace(/<\s*(\/?)\s*([a-zA-Z0-9-]+)((?:\s[^<>]*)?)\/?>/g, (_match, slash: string, tag: string, attrs: string) => {
      const name = tag.toLowerCase();
      if (!allowed.has(name)) return "";
      if (slash) return `</${name}>`;

      const safeAttrs: string[] = [];
      const attrRe = /([a-zA-Z-]+)\s*=\s*"([^"]*)"/g;
      let m: RegExpExecArray | null;
      while ((m = attrRe.exec(attrs)) !== null) {
        const attr = m[1].toLowerCase();
        const value = m[2];
        if (!allowedAttrs.has(attr)) continue;
        if (attr.startsWith("on")) continue;
        if ((attr === "href" || attr === "src") && !/^https?:\/\//i.test(value)) continue;
        safeAttrs.push(`${attr}="${value.replace(/"/g, "&quot;")}"`);
      }
      return `<${name}${safeAttrs.length ? " " + safeAttrs.join(" ") : ""}>`;
    });
}
